import IRepository from "data/IRepository";
import { useInjection } from "infrastructure/ioc/ioc.react";
import ExportParamsDTO from "features/exports/dtos/ExportParamsDTO";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { trackPromise } from "react-promise-tracker";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import { Exports_Export } from "data/endpoints";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { getRouteParams } from "features/shared/helpers/routeParams";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { isSuccessStatus } from "data/helpers/responseHelper";
import React from "react";
import ExportDocumentsSubDialog from "features/exports/components/ExportDocumentsSubDialog";
import { useTranslation } from "react-i18next";
import IFileService from "data/IFileService";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import ContentDialogAlert from "features/shared/components/contentDialog/ContentDialogAlert";

// models
export interface ExportHookProps {
  loadingArea?: string;
  showInfoDialog?: boolean;
}

export interface ExportHookResult {
  exportHandler: (
    exportDefinitionId: string,
    exportParams: ExportParamsDTO
  ) => Promise<void>;
}

export const useExport = ({
  loadingArea,
  showInfoDialog,
}: ExportHookProps): ExportHookResult => {
  // DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );
  const exportRepository = useInjection<IRepository<ExportParamsDTO>>(
    IocTypes.Repository
  );
  // translation
  const tLabels = useTranslation("exports\\exports").t;

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  // handlers
  const exportHandler = async (
    exportDefinitionId: string,
    exportParams: ExportParamsDTO
  ) => {
    const responsePromise = trackPromise(
      exportRepository.post<ActionResultDTO<DocumentSimplifiedDTO[]>>({
        url: Exports_Export,
        data: exportParams,
        params: getQueryParams("CustomerId", customer.customerId!),
        routeParams: getRouteParams("exportDefinitionId", exportDefinitionId),
      }),
        loadingArea,
    );

    openInfoDialog();

    const response = await responsePromise;

    if (isSuccessStatus(response) && response.data?.result?.length === 1) {
      // if is returned only one file - direct download it
      await fileService.downloadDocument(response.data.result[0]);
    } else {
      dialogService.openDialog(
        tLabels("exportDocumentsSubDialog.title"),
        () =>
          React.createElement(ExportDocumentsSubDialog, { response: response }),
        true
      );
    }
  };

  const openInfoDialog = () => {
    if (showInfoDialog) {
      dialogService.openDialog(
        tLabels("exportDocumentsSubDialog.title"),
        () =>
          React.createElement(ContentDialogAlert, {
            alertObject: {
              alertType: "Info",
              message: tLabels(
                "exportDocumentsSubDialog.downloadOnBackgroundInfoMessage"
              ),
            },
          }),
        true
      );      
    }
  };

  // result
  return {
    exportHandler: exportHandler,
  };
};
